import { AsyncPipe, DatePipe, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { BfmDownloadButtonComponent, BfmDownloadContract } from '@celerofinancas/common-domain';
import { CompanyService, TrackingService } from '@celerofinancas/service-utils';
import { DownloadFilters } from '@celerofinancas/ui-http-services';
import {
  BfmActiveFiltersComponent,
  BfmFinancialRecordsModalComponent,
  BfmPaginatedFinancialListComponent,
  ColumnItem,
  DATE_TYPE,
  FINANCIAL_RECORD_DOWNLOAD_CONTRACT,
  FINANCIAL_RECORD_LIST_TOKEN,
  PERIOD,
  SidebarFilter,
  StatusIcons,
} from '@celerofinancas/ui-cash-flow';
import { CommonInputsModule } from '@celerofinancas/ui-common';
import { ModalService } from '@celerofinancas/ui-modals';
import { HeaderAccountsComponent } from '@shared/components/header-accounts/header-accounts.component';
import { MainTitleComponent } from '@shared/components/main-title/main-title.component';
import { NavigationBarComponent } from '@shared/components/navigation-bar/navigation-bar.component';
import { BootstrapCoordinator } from '@shared/models/bootstrap-coordinator';
import { CAIXA_TRACKING_MODELS } from '@shared/models/tracking-models.enum';
import { CheckFirstAccessService } from '@shared/services/check-first-access/check-first-access.service';
import { FeatureFlagService } from '@celerofinancas/ui-feature-flag';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * StatementComponent is a component that represents the statement page
 */
@Component({
  selector: 'caixa-webcomponent-statement',
  standalone: true,
  imports: [
    AsyncPipe,
    DatePipe,
    BfmPaginatedFinancialListComponent,
    CommonInputsModule,
    NgStyle,
    BfmActiveFiltersComponent,
    MainTitleComponent,
    NavigationBarComponent,
    HeaderAccountsComponent,
    BfmDownloadButtonComponent
  ],
  templateUrl: './statement.component.html',
  styleUrl: './statement.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: FINANCIAL_RECORD_LIST_TOKEN,
      useValue: {
        recordNameFn: (
          r: { name?: string; bank_id?: string; company_related_name?: string | null },
        ) =>
          r.bank_id === '104' && r.company_related_name
            ? `${r.name} - ${r.company_related_name}`
            : r.name,
        nameMaxLines: 2,
      },
    },
  ],
})
export class StatementComponent extends BootstrapCoordinator implements OnInit {
  /**
   * Template for the magnifier icon
   */
  @ViewChild('magnifierTemplate', { read: TemplateRef, static: false })
  public magnifierTemplateRef: TemplateRef<any>;

  /**
   * Check First Access Service
   */
  private checkFirstAccess = inject(CheckFirstAccessService);

  /**
   * Global service for company-related operations
   */
  public company = inject(CompanyService);

  /**
   * Modal service for handling modals
   */
  private cdsModal = inject(ModalService);

  /**
   * Tracking service
   */
  private readonly tracking = inject(TrackingService);

  /**
   * Feature flag service
   */
  private readonly featureFlag: FeatureFlagService<string> = inject(FeatureFlagService);

  /**
   * Controls whether the download button should be visible
   */
  public readonly isDownloadEnabled$: Observable<boolean> =
    this.featureFlag.isFlagEnabled$('front_cashflow@download');

  /**
   * Export the download button asks for, the same one the records list exports
   */
  public readonly cashFlowContract: BfmDownloadContract = FINANCIAL_RECORD_DOWNLOAD_CONTRACT;

  /**
   * Controls active filters count
   */
  public activeFiltersCount$ = new BehaviorSubject(0);

  /**
   * Last update controller
   */
  public lastUpdate = signal(new Date().toISOString());

  /**
   * Headers for the statement table
   */
  public headers = {
    name: 'Nome',
    due_at: 'Vencimento',
    paid_at: 'Data',
    created_at: 'Envio',
    coa_name: 'Categoria',
    value: 'Valor (R$)',
    bank_id: 'Banco e Conta',
    processing_status: 'Status de Processamento',
    status: 'Status',
    magnifier: '',
  };

  /**
   * List of columns for the statement table
   */
  public columnsList: ColumnItem[] = [
    { name: 'status', size: 1, align: 'center' },
    { name: 'due_at', size: 2, align: 'center' },
    { name: 'name', size: 3, align: 'left' },
    { name: 'coa_name', size: 2, align: 'left' },
    { name: 'bank_id', size: 2, align: 'center' },
    { name: 'value', size: 1, align: 'center' },
    { name: 'custom', size: 1, align: 'center', template: () => this.magnifierTemplateRef },
  ];

  /**
   * Icons representing the status of the statement items
   */
  public statusIcons: StatusIcons = {
    done: { icon: 'cds-icon-filled-check', tooltip: 'Realizado' },
    predicted: { icon: 'cds-icon-filled-clock', tooltip: 'Previsto' },
    late: { icon: 'cds-icon-filled-alert-triangle', tooltip: 'Atrasado' },
  };

  /**
   * Date type filter
   */
  public dateType: SidebarFilter[] = [
    { id: 'payment', name: 'Liquidação', filter: 'payment', to: 'date_type', selected: false },
    { id: 'due', name: DATE_TYPE.due, filter: 'due', to: 'date_type', selected: false },
    {
      id: 'creation',
      name: DATE_TYPE.creation,
      filter: 'creation',
      to: 'date_type',
      selected: false,
    },
    { id: 'update', name: DATE_TYPE.update, filter: 'update', to: 'date_type', selected: false },
  ];

  /**
   * Date type filter
   */
  public period: SidebarFilter[] = [
    {
      name: PERIOD.personalized,
      selected: true,
      filter: '',
    },
    {
      name: PERIOD.yesterday,
      selected: false,
      filter: 'yesterday',
      to: ['date_range_start', 'date_range_end'],
    },
    {
      name: PERIOD.today,
      selected: false,
      filter: 'today',
      to: ['date_range_start', 'date_range_end'],
    },
    {
      name: PERIOD.tomorrow,
      selected: false,
      filter: 'tomorrow',
      to: ['date_range_start', 'date_range_end'],
    },
    {
      name: PERIOD.lastWeek,
      selected: false,
      filter: 'lastWeek',
      to: ['date_range_start', 'date_range_end'],
    },
    {
      name: PERIOD.thisWeek,
      selected: false,
      filter: 'thisWeek',
      to: ['date_range_start', 'date_range_end'],
    },
    {
      name: PERIOD.nextWeek,
      selected: false,
      filter: 'nextWeek',
      to: ['date_range_start', 'date_range_end'],
    },
    {
      name: PERIOD.lastMonth,
      selected: false,
      filter: 'lastMonth',
      to: ['date_range_start', 'date_range_end'],
    },
    {
      name: PERIOD.thisMonth,
      selected: false,
      filter: 'thisMonth',
      to: ['date_range_start', 'date_range_end'],
    },
    {
      name: PERIOD.nextMonth,
      selected: false,
      filter: 'nextMonth',
      to: ['date_range_start', 'date_range_end'],
    },
  ];

  /**
   * Financial Record Type
   */
  public recordType: SidebarFilter[] = [
    {
      name: 'Todos',
      filter: 'all',
      to: 'financial_record_type',
      selected: false,
    },
    {
      name: 'Somente entradas',
      filter: 'revenue',
      to: 'financial_record_type',
      selected: false,
    },
    {
      name: 'Somente saídas',
      filter: 'expense',
      to: 'financial_record_type',
      selected: false,
    },
  ];

  /**
   * Payment status object
   */
  public paymentStatus = [
    {
      name: 'Realizado',
      selected: false,
      filter: 'is_done',
      value: true,
      icon: 'cds-icon-calendar',
    },
    {
      name: 'Previsto',
      selected: false,
      filter: 'is_predicted',
      value: true,
      icon: 'cds-icon-eye',
    },
    {
      name: 'Atrasado',
      selected: false,
      filter: 'is_late',
      value: true,
      icon: 'cds-icon-calendar-slashed',
    },
  ];

  /**
   * Options for the modal that opens when a statement item is clicked
   */
  public modalOptions = {
    dateType: this.dateType,
    period: this.period,
    recordType: this.recordType,
    paymentStatus: this.paymentStatus,
  };

  /**
   * Applied modal filters
   */
  public appliedFilters: any = null;

  /**
   * Get search string
   */
  public get search(): string {
    return this.company.getFinancialRecordParams('search');
  }

  /**
   * Filters applied to the list, which the export repeats. Pagination and
   * ordering are dropped: they don't restrict the exported file
   */
  public get downloadFilters(): DownloadFilters {
    const { page, order, company_pk, only_processed, ...filters } =
      this.company.getFinancialRecordParams();

    return filters;
  }

  /**
   * Search financial records
   *
   * @param search String to be searched
   */
  public financialRecordSearch(search: string): void {
    this.tracking.track(CAIXA_TRACKING_MODELS.recordListSearchCaixa);
    const currentSearch = this.company.getFinancialRecordParams('search');
    this.company.setFinancialRecordParams(search, 'search');

    if (currentSearch || search) {
      this.company.applyPreferences();
    }
  }

  /**
   * Open sidebar
   */
  public openSidebar(): void {
    this.cdsModal.open(BfmFinancialRecordsModalComponent, {
      headerTitle: 'Selecionar os filtros',
      options: this.appliedFilters ?? this.modalOptions,
      applyFilters: (filters: any) => this.appliedFilters = filters,
    });
  }
}
